<script setup lang="ts">
import { computed, ref } from "vue"
import ClippedValue from "../components/ClippedValue.vue"
import CopyButton from "../components/CopyButton.vue"
import DashboardCard from "../components/DashboardCard.vue"
import DataState from "../components/DataState.vue"
import MetricTile from "../components/MetricTile.vue"
import NullBadge from "../components/NullBadge.vue"
import PageHeader from "../components/PageHeader.vue"
import SessionId from "../components/SessionId.vue"
import SessionInventoryTable from "../components/SessionInventoryTable.vue"
import StatusBadge from "../components/StatusBadge.vue"
import DashboardLayout from "../layouts/DashboardLayout.vue"
import { useDashboardStore } from "../stores/dashboard"
import { formatNumber, formatRelative } from "../utils/format"

type TableSorting = Array<{ id: string; desc: boolean }>

const dashboard = useDashboardStore()
const sessionSorting = ref<TableSorting>([{ id: "lastSeenAt", desc: true }])
const openSessions = computed(() =>
  dashboard.sessions.filter(
    (session) =>
      !session.closedAt &&
      session.status !== "closed" &&
      (session.credentialExpiresAt === null || session.credentialExpiresAt > dashboard.summary.generatedAt)
  )
)
const strictSessions = computed(() => dashboard.sessions.filter((session) => session.strict).length)
const stickySessions = computed(() => dashboard.sessions.filter((session) => session.stickyHashPresent).length)
const expiringSoon = computed(
  () =>
    dashboard.sessions.filter(
      (session) =>
        session.credentialExpiresAt !== null &&
        session.credentialExpiresAt > dashboard.summary.generatedAt &&
        session.credentialExpiresAt - dashboard.summary.generatedAt < 20 * 60_000
    ).length
)
const credentialHorizonMinutes = computed(() =>
  dashboard.credentialHealth.earliestExpiresAt === null
    ? null
    : Math.max(0, Math.round((dashboard.credentialHealth.earliestExpiresAt - dashboard.summary.generatedAt) / 60_000))
)
const credentialProgress = computed(() =>
  dashboard.credentialHealth.totalCredentials === 0
    ? 0
    : Math.round((dashboard.credentialHealth.activeCredentials / dashboard.credentialHealth.totalCredentials) * 100)
)
</script>

<template>
  <DashboardLayout>
    <div class="dashboard-data-page flex min-w-0 flex-col gap-5">
      <PageHeader
        title="Sessions and credentials"
        description="Sessions, credentials, and sticky assignments."
        details="Covers control plane session creation, active credential freshness, strict mode, and retained sticky-assignment evidence."
      >
        <template #actions>
          <StatusBadge status="credential_refreshed" />
          <span class="dashboard-mono text-sm text-(--dash-muted)">{{ formatNumber(openSessions.length) }} open</span>
        </template>
      </PageHeader>

      <DataState
        :loading="dashboard.isLoading"
        :error="dashboard.errorMessage"
        :empty="dashboard.sessions.length === 0"
        empty-title="No sessions"
        empty-description="No session records were returned by the Worker dashboard API."
      >
        <section class="dashboard-data-content flex min-w-0 flex-1 flex-col gap-4">
          <section class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <MetricTile
              label="Open sessions"
              :value="formatNumber(openSessions.length)"
              tone="success"
              caption="Active control plane records"
            />
            <MetricTile
              label="Strict sessions"
              :value="formatNumber(strictSessions)"
              tone="neutral"
              caption="Requested country is required"
            />
            <MetricTile
              label="Sticky sessions"
              :value="formatNumber(stickySessions)"
              tone="neutral"
              caption="Assignment continuity enabled"
            />
            <MetricTile
              label="Expiring soon"
              :value="formatNumber(expiringSoon)"
              tone="warning"
              caption="Credentials expiring within 20 min"
            />
          </section>

          <section class="dashboard-data-workspace grid min-w-0 flex-1 gap-4 xl:grid-cols-[minmax(0,1fr)_390px]">
            <SessionInventoryTable
              v-model:sorting="sessionSorting"
              :sessions="dashboard.sessions"
              :now="dashboard.summary.generatedAt"
              scroll-test-id="session-table-scroll"
              modal-scroll-test-id="session-table-modal-scroll"
            />

            <div class="grid gap-4">
              <DashboardCard title="Credential health" eyebrow="Refresh window" static>
                <div class="grid gap-4">
                  <div class="flex items-end justify-between gap-3">
                    <div>
                      <p class="dashboard-mono dashboard-value text-4xl font-medium">
                        {{ formatNumber(dashboard.credentialHealth.activeCredentials) }}
                      </p>
                      <p class="mt-1 text-sm text-(--dash-muted)">active credentials</p>
                    </div>
                    <StatusBadge :status="dashboard.credentialHealth.expiringWithin20Minutes ? 'warning' : 'ready'" />
                  </div>
                  <div class="dashboard-progress dashboard-progress--success">
                    <span :style="{ width: `${credentialProgress}%` }" />
                  </div>
                  <div class="grid gap-2 text-sm">
                    <div class="flex justify-between gap-3">
                      <span class="text-(--dash-muted)">Total credentials</span>
                      <span class="dashboard-mono">{{
                        formatNumber(dashboard.credentialHealth.totalCredentials)
                      }}</span>
                    </div>
                    <div class="flex justify-between gap-3">
                      <span class="text-(--dash-muted)">Refresh events</span>
                      <span class="dashboard-mono">{{ formatNumber(dashboard.credentialHealth.refreshEvents) }}</span>
                    </div>
                    <div class="flex justify-between gap-3">
                      <span class="text-(--dash-muted)">Expiry horizon</span>
                      <span v-if="credentialHorizonMinutes !== null" class="dashboard-mono">
                        {{ credentialHorizonMinutes }} min
                      </span>
                      <NullBadge v-else field="credential expiry" />
                    </div>
                  </div>
                </div>
              </DashboardCard>

              <DashboardCard title="Sticky assignment evidence" eyebrow="Broker decisions" static>
                <div v-if="dashboard.stickyAssignments.length" class="grid gap-3">
                  <article
                    v-for="assignment in dashboard.stickyAssignments.slice(0, 5)"
                    :key="assignment.id"
                    class="border-b border-(--dash-line) pb-3 last:border-b-0 last:pb-0"
                  >
                    <div class="flex items-center justify-between gap-3">
                      <SessionId :value="assignment.sessionId" max-class="max-w-[8rem]" />
                      <StatusBadge :status="assignment.stickyState" />
                    </div>
                    <p class="mt-2 text-sm leading-5 text-(--dash-muted)">{{ assignment.selectedReason }}</p>
                    <div class="mt-2 flex items-center gap-2 text-xs text-(--dash-dim)">
                      <ClippedValue :value="assignment.nodeId" max-class="max-w-[13rem]" title="Node id" />
                      <CopyButton :value="assignment.sessionId" label="Copy session id" />
                    </div>
                  </article>
                </div>
                <p v-else class="text-sm leading-5 text-(--dash-muted)">
                  No sticky mappings are currently retained in KV.
                </p>
              </DashboardCard>
            </div>
          </section>
        </section>
      </DataState>
    </div>
  </DashboardLayout>
</template>
